import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';
import { axiosInstance } from "../../routes/axiosInstance";
import { ArrowLeftIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import FormInput from '../../components/FormInput';
import PublicNav from '../../components/PublicNav';
import PublicFooter from '../../components/PublicFooter';

export default function SetPassword() {
  usePageTitle('Set password');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [passwordsMatch, setPasswordsMatch] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  });

  const navigate = useNavigate();
  const { token } = useParams();

  useEffect(() => {
    if (confirmPassword) {
      setPasswordsMatch(password === confirmPassword);
    }
  }, [password, confirmPassword]);

  useEffect(() => {
    const validations = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      // Any non-alphanumeric counts, matching the backend
      // PasswordComplexityValidator.
      specialChar: /[^A-Za-z0-9]/.test(password),
    };
    setPasswordValidation(validations);
  }, [password]);

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!Object.values(passwordValidation).every((isValid) => isValid)) {
      setError('Password does not meet the required criteria.');
      return;
    }

    // Equality check of two user-typed fields; no secret involved.
    // nosemgrep: eslint.detect-possible-timing-attacks, nodejs_scan.javascript-crypto-rule-node_timing_attack
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axiosInstance.post(`/user/password-reset/confirm`, {
        new_password: password,
        token,
      });

      if (response.status === 200) {
        setMessage('Password reset successful. Redirecting to sign in...');
        setTimeout(() => {
          navigate('/signin');
        }, 3000);
      }
    } catch (error) {
      // Parse validation errors from API response
      const responseData = error.response?.data || {};
      const validationErrors = responseData?.data || responseData;

      // Check for field-specific validation errors
      if (validationErrors && typeof validationErrors === 'object') {
        const errorFields = Object.entries(validationErrors).filter(([key, value]) =>
          Array.isArray(value) || typeof value === 'string'
        );

        if (errorFields.length > 0) {
          const errorMessage = errorFields
            .map(([field, messages]) => {
              const msgArray = Array.isArray(messages) ? messages : [messages];
              return msgArray.join('. ');
            })
            .join('. ');
          setError(errorMessage);
          return;
        }
      }

      // Fallback to generic error
      setError(responseData?.message || responseData?.error || 'Failed to reset password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const requirementClass = (met) =>
    met ? 'text-green-600 dark:text-green-400' : 'text-slate-500 dark:text-gray-400';

  return (
    <div className="min-h-screen bg-surface dark:bg-gray-870 text-slate-900 dark:text-gray-100 flex flex-col">
      <PublicNav/>

      <main className="flex-1 flex items-center justify-center w-full">
        <div className="max-w-md mx-auto px-6 py-16 w-full">
          <div className="text-center">
            <h1 className="text-xl sm:text-2xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white">
              Set a new <span className="italic text-teal-700 dark:text-teal-400">password</span>.
            </h1>
            <p className="mt-3 text-sm text-slate-600 dark:text-gray-300">
              Choose a strong password for your account.
            </p>
          </div>

          <div className="mt-10 rounded-2xl bg-surface/70 dark:bg-gray-870/60 backdrop-blur-sm border border-slate-900/10 dark:border-gray-100/10 p-6 sm:p-8">
            <form onSubmit={handleResetPassword} className="space-y-6">
              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-gray-300">
                  New password
                </label>
                <div className="relative">
                  <FormInput
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    autoFocus
                    variant="glass"
                    required
                    className="pr-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white transition"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" aria-hidden="true"/>
                    ) : (
                      <EyeIcon className="h-5 w-5" aria-hidden="true"/>
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-700 dark:text-gray-300">
                  Confirm password
                </label>
                <div className="relative">
                  <FormInput
                    id="confirm-password"
                    name="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    variant="glass"
                    required
                    className="pr-10"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white transition"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" aria-hidden="true"/>
                    ) : (
                      <EyeIcon className="h-5 w-5" aria-hidden="true"/>
                    )}
                  </button>
                </div>
              </div>

              {/* Password matching message */}
              {confirmPassword && (
                <p className={`text-sm ${passwordsMatch ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                </p>
              )}

              {/* Password complexity requirements */}
              <div className="text-sm text-slate-600 dark:text-gray-300">
                <p>Your password must meet the following requirements:</p>
                <ul className="mt-1 list-disc list-inside space-y-0.5">
                  <li className={requirementClass(passwordValidation.length)}>At least 8 characters long</li>
                  <li className={requirementClass(passwordValidation.uppercase)}>Includes an uppercase letter</li>
                  <li className={requirementClass(passwordValidation.lowercase)}>Includes a lowercase letter</li>
                  <li className={requirementClass(passwordValidation.number)}>Contains a number</li>
                  <li className={requirementClass(passwordValidation.specialChar)}>Has a special character</li>
                </ul>
              </div>

              {error && (
                <p role="alert" aria-live="assertive" className="text-red-600 dark:text-red-400 text-sm">
                  {error}
                </p>
              )}
              {message && (
                <p role="status" aria-live="polite" className="text-green-600 dark:text-green-400 text-sm">
                  {message}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="flex w-full justify-center items-center rounded-md bg-slate-900 text-white dark:bg-white dark:text-gray-900 px-4 py-2 text-sm font-medium h-10 shadow hover:opacity-90 transition focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-white disabled:opacity-60"
              >
                {submitting ? (
                  <svg className="animate-spin h-5 w-5 text-white dark:text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291a7.978 7.978 0 01-1.664-1.415A8.018 8.018 0 012.34 14H.12A9.985 9.985 0 006 19.29V17.29z"></path>
                  </svg>
                ) : (
                  'Set password'
                )}
              </button>
            </form>
          </div>

          <div className="mt-6 text-center">
            <Link to="/signin"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white transition">
              <ArrowLeftIcon className="h-4 w-4"/>
              Back to sign in
            </Link>
          </div>
        </div>
      </main>

      <PublicFooter/>
    </div>
  );
}
